import { 
  Project, User, LoginLog, Vessel, 
  Country, Contact, Order, Task
} from "../types";

/**
 * Nexus Hub Production API Service
 * Centralized gateway for PHP/MySQL backend interactions.
 */

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Network response was not ok' }));
    throw new Error(error.message || `HTTP Error: ${response.status}`);
  }
  return response.json();
};

export const apiService = {
    // Auth & Identity
    async login(username: string, password: string): Promise<User | null> {
        const response = await fetch('/api/login.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });
        const result = await handleResponse(response);
        return result.success ? result.user : null;
    },

    async getUsers(): Promise<User[]> {
        const response = await fetch('/api/users.php');
        return handleResponse(response);
    },

    async saveUser(user: User): Promise<boolean> {
        const response = await fetch('/api/users.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(user)
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteUser(id: string): Promise<boolean> {
        const response = await fetch(`/api/users.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    // Projects (Infrastructure)
    async getProjects(): Promise<Project[]> {
        const response = await fetch('/api/projects.php');
        return handleResponse(response);
    },

    async saveProject(project: Project): Promise<boolean> {
        const response = await fetch('/api/projects.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(project)
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteProject(id: string): Promise<boolean> {
        const response = await fetch(`/api/projects.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    // CRM Contacts
    async getContacts(): Promise<Contact[]> {
        const response = await fetch('/api/contacts.php');
        return handleResponse(response);
    },

    async saveContact(contact: Contact): Promise<boolean> {
        const response = await fetch('/api/contacts.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(contact)
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteContact(id: string): Promise<boolean> {
        const response = await fetch(`/api/contacts.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    // Port Vessels
    async getVessels(): Promise<Vessel[]> {
        const response = await fetch('/api/vessels.php');
        return handleResponse(response);
    },

    async saveVessel(vessel: Vessel): Promise<boolean> {
        const response = await fetch('/api/vessels.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(vessel)
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteVessel(id: string): Promise<boolean> {
        const response = await fetch(`/api/vessels.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    // Orders Pipeline
    async getOrders(): Promise<Order[]> {
        const response = await fetch('/api/orders.php');
        return handleResponse(response);
    },

    async saveOrder(order: Order): Promise<boolean> {
        const response = await fetch('/api/orders.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(order)
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteOrder(id: string): Promise<boolean> {
        const response = await fetch(`/api/orders.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    // Audit Logs
    async getLoginLogs(): Promise<LoginLog[]> {
        const response = await fetch('/api/logs.php');
        return handleResponse(response);
    },

    // Persistent Quotes & History
    async getQuotes(): Promise<any[]> {
        const response = await fetch('/api/quotes.php');
        return handleResponse(response);
    },

    async getQuoteById(id: string): Promise<{ data: any, id: string, alias: string }> {
        const response = await fetch(`/api/quotes.php?id=${id}`);
        const result = await handleResponse(response);
        return {
            id: result.id,
            alias: result.alias,
            data: JSON.parse(result.data)
        };
    },

    async saveQuote(quote: any, country: Country, alias: string, id?: string): Promise<boolean> {
        const response = await fetch('/api/quotes.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                id: id || null, // null will trigger unique ID generation in PHP
                quote, 
                country, 
                alias 
            })
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async deleteQuote(id: string): Promise<boolean> {
        const response = await fetch(`/api/quotes.php?id=${id}`, {
            method: 'DELETE'
        });
        const result = await handleResponse(response);
        return result.success;
    },

    async getNextQuoteNumber(country: Country): Promise<string> {
        const response = await fetch(`/api/quotes.php?action=next_number&country=${country}`);
        const result = await handleResponse(response);
        return result.number;
    },

    // Tasks Management
    async getTasks(country?: Country): Promise<Task[]> {
        const local = localStorage.getItem('nexus_db_tasks');
        const tasks: Task[] = local ? JSON.parse(local) : [];
        if (country) return tasks.filter(t => t.country === country);
        return tasks;
    },

    async saveTask(task: Task): Promise<boolean> {
        const local = localStorage.getItem('nexus_db_tasks');
        const tasks: Task[] = local ? JSON.parse(local) : [];
        const next = tasks.some(t => t.id === task.id) 
            ? tasks.map(t => t.id === task.id ? task : t) 
            : [task, ...tasks];
        localStorage.setItem('nexus_db_tasks', JSON.stringify(next));
        return true;
    },

    async deleteTask(id: string): Promise<boolean> {
        const local = localStorage.getItem('nexus_db_tasks');
        const tasks: Task[] = local ? JSON.parse(local) : [];
        const next = tasks.filter(t => t.id !== id);
        localStorage.setItem('nexus_db_tasks', JSON.stringify(next));
        return true;
    }
};